/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `connectivity` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class ConnectivityMesg : Mesg {
    public constructor() : super("Connectivity", Profile.MesgNum.CONNECTIVITY)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** bluetooth_enabled. Use Bluetooth for connectivity features */
    public var bluetoothEnabled: Boolean?
        get() = (getFieldValue(BLUETOOTH_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(BLUETOOTH_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** bluetooth_le_enabled. Use Bluetooth Low Energy for connectivity features */
    public var bluetoothLeEnabled: Boolean?
        get() = (getFieldValue(BLUETOOTH_LE_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(BLUETOOTH_LE_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** ant_enabled. Use ANT for connectivity features */
    public var antEnabled: Boolean?
        get() = (getFieldValue(ANT_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(ANT_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** name. */
    public var name: String?
        get() = getFieldValue(NAME_FIELD_NUM) as? String
        set(value) = setFieldValue(NAME_FIELD_NUM, value)

    /** live_tracking_enabled. */
    public var liveTrackingEnabled: Boolean?
        get() = (getFieldValue(LIVE_TRACKING_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(LIVE_TRACKING_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** weather_conditions_enabled. */
    public var weatherConditionsEnabled: Boolean?
        get() = (getFieldValue(WEATHER_CONDITIONS_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(WEATHER_CONDITIONS_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** weather_alerts_enabled. */
    public var weatherAlertsEnabled: Boolean?
        get() = (getFieldValue(WEATHER_ALERTS_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(WEATHER_ALERTS_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** auto_activity_upload_enabled. */
    public var autoActivityUploadEnabled: Boolean?
        get() = (getFieldValue(AUTO_ACTIVITY_UPLOAD_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(AUTO_ACTIVITY_UPLOAD_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** course_download_enabled. */
    public var courseDownloadEnabled: Boolean?
        get() = (getFieldValue(COURSE_DOWNLOAD_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(COURSE_DOWNLOAD_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** workout_download_enabled. */
    public var workoutDownloadEnabled: Boolean?
        get() = (getFieldValue(WORKOUT_DOWNLOAD_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(WORKOUT_DOWNLOAD_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** gps_ephemeris_download_enabled. */
    public var gpsEphemerisDownloadEnabled: Boolean?
        get() = (getFieldValue(GPS_EPHEMERIS_DOWNLOAD_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(GPS_EPHEMERIS_DOWNLOAD_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** incident_detection_enabled. */
    public var incidentDetectionEnabled: Boolean?
        get() = (getFieldValue(INCIDENT_DETECTION_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(INCIDENT_DETECTION_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** grouptrack_enabled. */
    public var grouptrackEnabled: Boolean?
        get() = (getFieldValue(GROUPTRACK_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(GROUPTRACK_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    public companion object {
        public const val BLUETOOTH_ENABLED_FIELD_NUM: UByte = 0u
        public const val BLUETOOTH_LE_ENABLED_FIELD_NUM: UByte = 1u
        public const val ANT_ENABLED_FIELD_NUM: UByte = 2u
        public const val NAME_FIELD_NUM: UByte = 3u
        public const val LIVE_TRACKING_ENABLED_FIELD_NUM: UByte = 4u
        public const val WEATHER_CONDITIONS_ENABLED_FIELD_NUM: UByte = 5u
        public const val WEATHER_ALERTS_ENABLED_FIELD_NUM: UByte = 6u
        public const val AUTO_ACTIVITY_UPLOAD_ENABLED_FIELD_NUM: UByte = 7u
        public const val COURSE_DOWNLOAD_ENABLED_FIELD_NUM: UByte = 8u
        public const val WORKOUT_DOWNLOAD_ENABLED_FIELD_NUM: UByte = 9u
        public const val GPS_EPHEMERIS_DOWNLOAD_ENABLED_FIELD_NUM: UByte = 10u
        public const val INCIDENT_DETECTION_ENABLED_FIELD_NUM: UByte = 11u
        public const val GROUPTRACK_ENABLED_FIELD_NUM: UByte = 12u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[0] = Field("BluetoothEnabled", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("BluetoothLeEnabled", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("AntEnabled", 2u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[3] = Field("Name", 3u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[4] = Field("LiveTrackingEnabled", 4u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[5] = Field("WeatherConditionsEnabled", 5u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[6] = Field("WeatherAlertsEnabled", 6u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[7] = Field("AutoActivityUploadEnabled", 7u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[8] = Field("CourseDownloadEnabled", 8u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[9] = Field("WorkoutDownloadEnabled", 9u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[10] = Field("GpsEphemerisDownloadEnabled", 10u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[11] = Field("IncidentDetectionEnabled", 11u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[12] = Field("GrouptrackEnabled", 12u, BaseType.ENUM, 1.0, 0.0, "", false)
            return fields
        }
    }
}
